import { rotateBoundingBoxViaCanvas } from "../canvas-rotation";
import {
    ChunkMatrix,
    clusterChunks,
    ClusterChunksParameters,
    mergeOverlappingPossibleBarcodes,
    OverlappingBarcodesParameters,
    PossibleBarcode,
} from "../chunk-clustering";
import { ChunkDimensions, chunkImage } from "../chunks";
import { decodeBarcodeDirectImageDataFirstLine, DecodeParameters, DecodeResult } from "../decoder";
import { ColorSpace } from "../locator/color-spaces";
import {
    fullChunkRequestMessageName,
    ChunkAngleResponseMesssage,
    preThresholdRequestMessageName,
    UnifiedLocationRequestMessage,
    ProcessFrameResult,
    DecodeBarcodeRequestMessage,
    BarcodeResponseMessage,
    isUnifiedLocationRequestMessage,
    decodeBarcodeRequestMessageName,
    isMessagePacketCommon,
    isChunkAngleResponseMessage,
    isBarcodeResponseMessage,
    PossibleBarcodeImageResult,
} from "./types";

let nextClientID = 0;

type WorkerImpl = Worker | Window;
type WorkerConstructor = (id: number) => WorkerImpl;
type ClientLocationCallback = (resp: ChunkAngleResponseMesssage) => void;
type ClientDecodeCallback = (resp: BarcodeResponseMessage) => void;

type ClientCallback = ClientLocationCallback | ClientDecodeCallback;

type ClientRequest =
    | {
          req: UnifiedLocationRequestMessage;
          responseFunc: ClientLocationCallback;
      }
    | {
          req: DecodeBarcodeRequestMessage;
          responseFunc: ClientDecodeCallback;
      };

type ClientRequestConstructor = (offset: number, total: number) => ClientRequest | undefined;

export class BarcodeWorkerPool {
    private workers = new Map<number, WorkerImpl>();
    private clientCallbacks = new Map<number, ClientCallback[]>();

    private notifyClientOfMessage(clientID: number, response: ChunkAngleResponseMesssage | BarcodeResponseMessage) {
        const clients = this.clientCallbacks.get(clientID);
        const nextUp = clients?.shift();
        if (nextUp === undefined) {
            return;
        }
        nextUp(response as any);
        if ((clients?.length ?? 0) === 0) {
            this.clientCallbacks.delete(clientID);
        }
    }

    private onWorkerMessage = (ev: Event) => {
        const message = (ev as any).data;
        if (!isMessagePacketCommon(message)) return;
        if (!isChunkAngleResponseMessage(message) && !isBarcodeResponseMessage(message)) return;

        const { dstClientID } = message;
        this.notifyClientOfMessage(dstClientID, message);
    };

    private onWorkerError = (ev: ErrorEvent) => {
        this.close();
        this.errorHandler?.(ev);
    };

    public close() {
        for (const worker of this.workers.values()) {
            if ("terminate" in worker && typeof worker.terminate === "function") {
                worker.terminate();
            } else {
                worker.removeEventListener("message", this.onWorkerMessage);
                worker.removeEventListener("error", this.onWorkerError as any);
            }
        }
        this.workers.clear();
    }

    constructor(
        public readonly size: number,
        mkworker: WorkerConstructor,
        private readonly errorHandler?: (ev: ErrorEvent) => void
    ) {
        try {
            for (let i = 0; i < size; i++) {
                const clientID = nextClientID++;
                const worker = mkworker(clientID);
                worker.addEventListener("message", this.onWorkerMessage);
                worker.addEventListener("error", this.onWorkerError as any);
                this.workers.set(clientID, worker);
            }
        } catch (e: unknown) {
            this.close();
            throw e;
        }
    }

    public forWorkers(requestFunc: ClientRequestConstructor) {
        let i = 0;
        for (const specs of this.workers.entries()) {
            const workerID = specs[0];
            const worker = specs[1];
            const clientRequest = requestFunc(i, this.workers.size);

            if (clientRequest === undefined) continue;

            const { req, responseFunc } = clientRequest;
            const { srcClientID } = req;
            i++;

            let callbacks = this.clientCallbacks.get(srcClientID);
            if (callbacks === undefined) {
                callbacks = [];
                this.clientCallbacks.set(srcClientID, callbacks);
            }
            callbacks.push(responseFunc);
            if (worker instanceof Worker) {
                worker.postMessage(
                    {
                        ...req,
                        dstClientID: workerID,
                    },
                    {
                        transfer: [isUnifiedLocationRequestMessage(req) ? req.buffers : req.imgBuffer],
                    }
                );
            } else {
                worker.postMessage(
                    {
                        ...req,
                        dstClientID: workerID,
                    },
                    worker.origin
                );
            }
        }
    }
}

function bucketLineSizeForBucketImageReshape(height: number, chunkDim: number, buckets: number): number {
    const chunkLines = Math.ceil(height / chunkDim);
    return Math.ceil(chunkLines / buckets);
}

export function bucketSizeForBucketImageReshape(
    width: number,
    height: number,
    chunkDim: number,
    buckets: number
): number {
    const lineSize = bucketLineSizeForBucketImageReshape(height, chunkDim, buckets);
    const chunksPerLine = Math.ceil(width / chunkDim);
    return lineSize * chunksPerLine;
}

export function bufferSizeForBucketImageReshape(
    width: number,
    height: number,
    chunkDim: number,
    buckets: number
): number {
    return bucketLineSizeForBucketImageReshape(height, chunkDim, buckets) * chunkDim * width;
}

type ClientParams = Readonly<{
    pool: BarcodeWorkerPool;
    srcCtx: CanvasRenderingContext2D;
    procCtx: CanvasRenderingContext2D;
    chunkDimension: number;
    clusterParams: ClusterChunksParameters;
    outputPadding: number;
}>;

type ClientDecodeParams = Readonly<{
    yStart: number;
    yMin: number;
    yMax: number;
}>;

export class BarcodeWorkerClient {
    private readonly clientID: number = nextClientID++;
    private chunkSchedule: ChunkDimensions[] = [];
    private chunkMatrix: ChunkMatrix = new ChunkMatrix(0, 0, 0, 0);
    private width: number = 0;
    private height: number = 0;
    private chunkDimension: number;

    private resetDimensions() {
        const { srcCtx } = this.params;
        const { chunkDimension } = this;
        const { canvas } = srcCtx;
        if (canvas.width === this.width && canvas.height === this.height) return;
        this.width = Math.floor(canvas.width);
        this.height = Math.floor(canvas.height);
        this.chunkMatrix = new ChunkMatrix(
            Math.ceil(this.width / chunkDimension),
            Math.ceil(this.height / chunkDimension),
            chunkDimension,
            chunkDimension
        );
        this.chunkSchedule = chunkImage(this.width, this.height, chunkDimension);
    }

    constructor(public readonly params: ClientParams) {
        this.chunkDimension = params.chunkDimension;
        this.resetDimensions();
    }

    public resetChunkDimension(chunkDimension: number): BarcodeWorkerClient {
        this.chunkDimension = chunkDimension;
        this.resetDimensions();
        return this;
    }

    private async sendWorkerLocationRequest(
        colorSpace: ColorSpace,
        srcArray: Uint8Array,
        srcWidth: number,
        srcHeight: number,
        chunkDim: number,
        chunkSchedule: readonly ChunkDimensions[],
        mat: ChunkMatrix,
        kind: typeof fullChunkRequestMessageName | typeof preThresholdRequestMessageName
    ) {
        let requestsZeroCallback: (() => void) | undefined;

        const requestsDone = new Promise<void>(resolve => {
            requestsZeroCallback = resolve;
        });

        const { pool, clusterParams } = this.params;
        const { angleEpsilon } = clusterParams;
        const buffersSize = bufferSizeForBucketImageReshape(srcWidth, srcHeight, chunkDim, pool.size) * 4;
        const bucketSize = bucketSizeForBucketImageReshape(srcWidth, srcHeight, chunkDim, pool.size);

        let outstandingRequests = 0;

        pool.forWorkers(workerOffset => {
            const chunkScheduleOffset = workerOffset * bucketSize;
            const baseBufOffset = workerOffset * buffersSize;

            if (baseBufOffset >= srcArray.length) return undefined;

            const buffers = srcArray.slice(
                baseBufOffset,
                baseBufOffset + Math.min(buffersSize, srcArray.length - baseBufOffset)
            );

            const chunksToProcess = Math.min(bucketSize, chunkSchedule.length - chunkScheduleOffset);

            outstandingRequests++;
            return {
                req: {
                    dstClientID: 0,
                    srcClientID: this.clientID,
                    kind,
                    buffers: buffers.buffer,
                    colorSpace,
                    imgWidth: srcWidth,
                    imgHeight: srcHeight,
                    chunkDim,
                    chunkScheduleOffset,
                    chunksToProcess,
                    angleDelta: angleEpsilon,
                },
                responseFunc: ({ responses }: ChunkAngleResponseMesssage) => {
                    for (const { xOffset, yOffset, angleCluster } of responses) {
                        mat.set(xOffset / mat.xStep, yOffset / mat.yStep, angleCluster);
                    }
                    outstandingRequests--;
                    if (outstandingRequests <= 0) {
                        outstandingRequests = 0;
                        requestsZeroCallback?.();
                    }
                },
            };
        });

        await requestsDone;
    }

    public async locateBarcodes(colorSpace: ColorSpace): Promise<ProcessFrameResult> {
        this.resetDimensions();

        const { srcCtx, clusterParams, outputPadding, procCtx } = this.params;
        const { chunkDimension } = this;

        const srcData = srcCtx.getImageData(0, 0, this.width, this.height);
        const srcArray = new Uint8Array(srcData.data.buffer);

        // Phase 1: Process the image entirely within the chunks. Particularly,
        // thresholding happens on a per-chunk basis.
        this.chunkMatrix.reset();
        await this.sendWorkerLocationRequest(
            colorSpace,
            srcArray,
            this.width,
            this.height,
            chunkDimension,
            this.chunkSchedule,
            this.chunkMatrix,
            fullChunkRequestMessageName
        );

        const firstPassBarcodes = clusterChunks(this.chunkMatrix, clusterParams);
        const possibleBarcodes: PossibleBarcode[] = [];
        const barcodesImageData: ImageData[] = [];

        // Phase 2: Within each of the found barcodes, threshold for the entire
        // bounding box and then process individually. This should reduce the
        // overall size (and increase the output quality) of the resulting
        // barcodes.
        for (const barcode of firstPassBarcodes) {
            const { boundingBox } = barcode;
            const { width: boxWidth, height: boxHeight } = boundingBox;

            const { canvas: procCanvas } = procCtx;
            procCanvas.width = boxWidth + 2 * outputPadding;
            procCanvas.height = boxHeight + 2 * outputPadding;

            const { imageData } = rotateBoundingBoxViaCanvas(srcCtx, procCtx, boundingBox, outputPadding);

            possibleBarcodes.push(barcode);
            barcodesImageData.push(imageData);
        }

        return {
            possibleBarcodes,
            barcodesImageData,
            imageData: srcData,
        };
    }

    public mergeOverlappingPossibleBarcodes(
        pbs: readonly PossibleBarcode[],
        imageDatas: readonly ImageData[],
        overlapParams: OverlappingBarcodesParameters
    ): PossibleBarcodeImageResult {
        const { outputPadding, srcCtx, procCtx } = this.params;
        const origIndices = new Map<PossibleBarcode, number>();
        for (let i = 0; i < pbs.length; i++) {
            origIndices.set(pbs[i], i);
        }

        // Note that we're relying on preserved identity for unmerged barcodes.
        const possibleBarcodes = mergeOverlappingPossibleBarcodes(pbs, overlapParams);
        const barcodesImageData: ImageData[] = [];

        const { canvas } = procCtx;

        for (const pb of possibleBarcodes) {
            const origIndex = origIndices.get(pb);

            // Rotate via canvas is pretty expensive. We only should do so if we
            // absolutely have to.
            if (origIndex !== undefined) {
                const origImageData = imageDatas[origIndex];
                if (origImageData !== undefined) {
                    barcodesImageData.push(origImageData);
                    continue;
                }
            }

            const { boundingBox } = pb;
            canvas.width = boundingBox.width + outputPadding;
            canvas.height = boundingBox.height + outputPadding;

            const { imageData } = rotateBoundingBoxViaCanvas(srcCtx, procCtx, boundingBox, outputPadding);
            barcodesImageData.push(imageData);
        }

        return { possibleBarcodes, barcodesImageData };
    }

    public async decodeFromImageData(
        imgData: ImageData,
        colorSpaces: ColorSpace[],
        decodeParams: DecodeParameters,
        { yStart, yMin, yMax }: ClientDecodeParams
    ): Promise<DecodeResult | undefined> {
        if (colorSpaces.length < 1) return undefined;

        const maybeResult = decodeBarcodeDirectImageDataFirstLine(imgData, colorSpaces, decodeParams, yStart);
        if (maybeResult !== undefined) {
            return maybeResult;
        }

        const { decodeStep } = decodeParams;
        const { pool } = this.params;
        const iterationStep = decodeStep * pool.size;
        let below = yStart - decodeStep,
            above = yStart + decodeStep;
        while (yMin <= below || above <= yMax) {
            for (const sign of [-1, 1]) {
                if (sign === -1 && below < yMin) continue;
                if (sign === 1 && above > yMax) continue;

                let outstandingRequests = 0;
                let requestsZeroCallback: (() => void) | undefined;
                const requestsDone = new Promise<void>(resolve => {
                    requestsZeroCallback = resolve;
                });

                const results: DecodeResult[] = [];
                let poolOffset = below;
                if (sign === 1) {
                    poolOffset = above;
                }

                pool.forWorkers(workerOffset => {
                    let yOffset: number;
                    if (sign === -1) {
                        yOffset = poolOffset - workerOffset * decodeStep;
                    } else {
                        yOffset = poolOffset + workerOffset * decodeStep;
                    }
                    if (yOffset < 0 || imgData.height <= yOffset) {
                        return undefined;
                    }

                    const imgBuffer = imgData.data.slice(yOffset * imgData.width * 4, (yOffset + 1) * imgData.width * 4)
                        .buffer;
                    outstandingRequests++;
                    return {
                        req: {
                            kind: decodeBarcodeRequestMessageName,
                            imgWidth: imgData.width,
                            imgBuffer,
                            colorSpaces,
                            decodeParams,
                            srcClientID: this.clientID,
                            dstClientID: 0,
                        },
                        responseFunc: ({ result }: BarcodeResponseMessage) => {
                            if (result !== undefined) {
                                results.push(result);
                            }
                            outstandingRequests--;
                            if (outstandingRequests <= 0) {
                                outstandingRequests = 0;
                                requestsZeroCallback?.();
                            }
                        },
                    };
                });

                if (outstandingRequests > 0) {
                    await requestsDone;
                }

                if (sign === -1) {
                    below -= iterationStep;
                } else {
                    above += iterationStep;
                }

                if (results.length === 0) {
                    continue;
                }

                let canReturn = true;
                const firstResult = results[0];
                for (let i = 1; i < results.length; i++) {
                    const result = results[i];
                    if (result.code !== firstResult.code || result.standard !== firstResult.standard) {
                        canReturn = false;
                        break;
                    }
                }

                if (canReturn) {
                    return results[0];
                }
            }
        }
        return undefined;
    }
}
